import useEmblaCarousel from "embla-carousel-react";
import * as React from "react";
import { reducer } from "./reducer";
import { initializeState } from "./state";
const createId = () => String(Date.now().toString(32) + Math.random().toString(16)).replace(/\./g, "");
const useIdFallback = () => {
    const [state] = React.useState(createId);
    return state;
};
const useId = React.useId || useIdFallback;
export const useCarouselReducer = ({ direction, align, axis, loop, draggable, breakpoints, currentSlide, }) => {
    const carouselId = `carousel-${useId()}`;
    const [startIndex] = React.useState(currentSlide ?? 0);
    const [setViewportRef, emblaAPI] = useEmblaCarousel({
        loop,
        draggable,
        align,
        axis,
        direction,
        breakpoints,
        containScroll: "trimSnaps",
        startIndex,
    });
    const [state, dispatch] = React.useReducer(reducer, { emblaAPI, activeSlideIndex: startIndex, carouselId }, initializeState);
    const { activeSlideIndex } = state;
    const lastTwoUserEvents = React.useRef([]);
    const setLastUserEvent = (lastEvent) => {
        lastTwoUserEvents.current = [lastTwoUserEvents.current[1], lastEvent];
    };
    const wasScrolledByUser = () => {
        return lastTwoUserEvents.current.includes("scroll");
    };
    const updateCarouselState = React.useCallback(() => {
        if (!emblaAPI) {
            return;
        }
        const newActiveSlideIndex = emblaAPI.selectedScrollSnap();
        const nSlides = emblaAPI.slideNodes().length;
        const canScrollNext = currentSlide === undefined ? emblaAPI.canScrollNext() : currentSlide < nSlides - 1;
        const props = {
            canScrollNext,
            canScrollPrev: emblaAPI.canScrollPrev(),
            scrollProgress: (100 * emblaAPI.selectedScrollSnap()) / (emblaAPI.scrollSnapList().length - 1),
            nSlides,
        };
        if (wasScrolledByUser()) {
            props.activeSlideIndex = newActiveSlideIndex;
        }
        dispatch({
            type: "setProps",
            props,
        });
    }, [currentSlide, emblaAPI]);
    React.useEffect(() => {
        if (!emblaAPI) {
            return;
        }
        dispatch({
            type: "setProps",
            props: {
                emblaAPI,
                activeSlideIndex: startIndex,
                canScrollNext: emblaAPI.canScrollNext(),
                canScrollPrev: emblaAPI.canScrollPrev(),
                nSlides: emblaAPI.slideNodes().length,
            },
        });
    }, [emblaAPI, startIndex]);
    React.useEffect(() => {
        if (!emblaAPI) {
            return;
        }
        emblaAPI.reInit({ loop, draggable, align, direction, axis, breakpoints });
        dispatch({
            type: "setProps",
            props: {
                axis,
                loop,
                direction,
                draggable,
                breakpoints,
            },
        });
    }, [emblaAPI, loop, draggable, align, direction, axis, breakpoints]);
    React.useEffect(() => {
        if (!emblaAPI) {
            return;
        }
        if (currentSlide !== undefined && startIndex !== currentSlide) {
            updateCarouselState();
        }
        const onScrollBetweenClicks = () => {
            setLastUserEvent("scroll");
        };
        const onPointerDown = () => {
            emblaAPI.on("scroll", onScrollBetweenClicks);
            setLastUserEvent("pointerDown");
        };
        const onPointerUp = () => {
            emblaAPI.off("scroll", onScrollBetweenClicks);
            setLastUserEvent("pointerUp");
        };
        emblaAPI.on("select", updateCarouselState);
        emblaAPI.on("resize", updateCarouselState);
        emblaAPI.on("pointerDown", onPointerDown);
        emblaAPI.on("pointerUp", onPointerUp);
        return () => {
            emblaAPI.off("select", updateCarouselState);
            emblaAPI.off("resize", updateCarouselState);
            emblaAPI.off("pointerDown", onPointerDown);
            emblaAPI.off("pointerUp", onPointerUp);
        };
    }, [currentSlide, emblaAPI, startIndex, updateCarouselState]);
    React.useEffect(() => {
        if (!emblaAPI) {
            return;
        }
        emblaAPI.scrollTo(activeSlideIndex);
    }, [emblaAPI, activeSlideIndex]);
    return [state, dispatch, setViewportRef];
};
//# sourceMappingURL=hooks.js.map